import {
  Faceted_Load_Filter,
  LoadDigestEmailDraft_Read,
  LoadState,
} from '@treadinc/horizon-api-spec';

import {
  OutboundEmailDraft,
  OutboundEmailDraftFields,
} from '~hooks/useOutboundEmails/models';
import prefixKeys from '~utils/prefixKeys';

import buildDispatchSearchRequest, {
  toDispatchOrdersQueryParams,
} from '../useOrders/buildDispatchSearchRequest';

export enum LoadDigestLoadFilter {
  ALL = 'all',
  APPROVED = 'approved',
  COMPLETED = 'completed',
}

const loadFilters = {
  [LoadDigestLoadFilter.ALL]: undefined,
  [LoadDigestLoadFilter.APPROVED]: { billing_approved: true },
  [LoadDigestLoadFilter.COMPLETED]: { states: [LoadState.COMPLETED] },
} satisfies Record<LoadDigestLoadFilter, Faceted_Load_Filter | undefined>;

type DispatchSearch = ReturnType<typeof buildDispatchSearchRequest>;

export type LoadDigestSelection =
  | { orderId: string }
  | { jobIds: string[] }
  | { dispatchSearch: DispatchSearch };

// Order and job selections take the load filter at filter.load, the dispatch search at filter.job.load.
export function toLoadFilterBody(loadFilter: LoadDigestLoadFilter) {
  const load = loadFilters[loadFilter];

  return load ? { filter: { load } } : {};
}

export function withJobLoadFilter(
  dispatchSearch: DispatchSearch,
  loadFilter: LoadDigestLoadFilter,
): DispatchSearch {
  const load = loadFilters[loadFilter];

  if (!load) {
    return dispatchSearch;
  }

  return {
    ...dispatchSearch,
    filter: { ...dispatchSearch.filter, job: { ...dispatchSearch.filter?.job, load } },
  };
}

export function toLoadDigestSelectionBody(
  selection: LoadDigestSelection,
  loadFilter: LoadDigestLoadFilter,
) {
  if ('orderId' in selection) {
    return { order_id: selection.orderId, ...toLoadFilterBody(loadFilter) };
  }

  if ('jobIds' in selection) {
    return { job_ids: selection.jobIds, ...toLoadFilterBody(loadFilter) };
  }

  return withJobLoadFilter(selection.dispatchSearch, loadFilter);
}

export function toLoadDigestSelectionQuery(
  selection: LoadDigestSelection,
  loadFilter: LoadDigestLoadFilter,
) {
  if ('dispatchSearch' in selection) {
    return toDispatchOrdersQueryParams(
      withJobLoadFilter(selection.dispatchSearch, loadFilter),
    );
  }

  const load = loadFilters[loadFilter];
  const selectionQuery =
    'orderId' in selection
      ? { order_id: selection.orderId }
      : { job_ids: selection.jobIds };

  return { ...selectionQuery, ...(load ? prefixKeys('filter[load]', load) : {}) };
}

export class LoadDigestEmailDraft extends OutboundEmailDraft {
  constructor(
    private _order_ids: string[],
    draft: OutboundEmailDraftFields,
  ) {
    super(draft);
  }

  public static parse(proto: LoadDigestEmailDraft_Read) {
    return new LoadDigestEmailDraft(
      proto.order_ids,
      OutboundEmailDraft.parseFields(proto),
    );
  }

  public get ids() {
    return this._order_ids;
  }
}
